import { ArrowUpRight, Inbox, Sparkles } from "lucide-react"
import * as React from "react"

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

import type { AuthorSide, Finding, Message } from "../data/review-data"

/**
 * A glance-and-dismiss popover anchored to the messages icon in the
 * header.
 *
 * The design invariant behind it: not every look at messages is a working
 * session. Most of the time a reviewer wants to see "is there anything
 * new, and roughly what" without leaving the finding they are on. That's
 * a popover, not a sheet.
 *
 * When the reviewer actually needs to compose or scroll history, the
 * "Open full messages" button promotes to the side sheet — the popover
 * becomes the doorway, not the destination.
 */
export interface MessagesPopoverProps {
  messages: readonly Message[]
  findings: Finding[]
  onOpenFullSheet: () => void
  children: React.ReactNode
}

const SIDE_TAG: Record<AuthorSide, { label: string; className: string }> = {
  reviewer: { label: "Reviewer", className: "bg-surface-sunken text-text-2" },
  client: { label: "Client", className: "bg-print-tint text-print" },
  ai: { label: "AI", className: "bg-info-tint text-info" },
}

export function MessagesPopover({
  messages,
  findings,
  onOpenFullSheet,
  children,
}: MessagesPopoverProps): JSX.Element {
  const [open, setOpen] = React.useState(false)

  // Most-recent three, from the tail. Reversed to show newest first.
  const recent = React.useMemo(
    () =>
      [...messages].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()).slice(0, 3),
    [messages],
  )

  const openFull = () => {
    setOpen(false)
    onOpenFullSheet()
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-[360px] overflow-hidden p-0">
        <header className="border-b border-border-soft px-4 py-3">
          <p className="text-body-sm font-semibold text-text">Recent messages</p>
          <p className="mt-0.5 text-caption text-text-3">
            {messages.length === 0
              ? "Nothing yet"
              : `${messages.length} on this review, newest first`}
          </p>
        </header>

        {recent.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-8 text-center">
            <span className="grid size-9 place-items-center rounded-full bg-surface-sunken text-text-3">
              <Inbox className="size-4" aria-hidden="true" />
            </span>
            <p className="text-body-sm font-medium text-text">No messages yet</p>
            <p className="max-w-[26ch] text-caption leading-[1.5] text-text-3">
              Start a thread from a finding or send a general note.
            </p>
            <button
              type="button"
              onClick={openFull}
              className="mt-1 rounded-sm px-2 py-1 text-caption font-medium text-brand transition-colors duration-120 ease-out hover:bg-brand-tint focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]"
            >
              Write one
            </button>
          </div>
        ) : (
          <ul className="max-h-[320px] divide-y divide-border-soft overflow-auto">
            {recent.map((m) => (
              <MessageRow key={m.id} message={m} findings={findings} onOpen={openFull} />
            ))}
          </ul>
        )}

        <footer className="flex items-center justify-between gap-3 border-t border-border-soft bg-surface-sunken/40 px-4 py-2.5">
          <span className="min-w-0 truncate font-id text-[10px] uppercase tracking-[0.1em] text-text-3">
            Visible to everyone
          </span>
          <button
            type="button"
            onClick={openFull}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-sm px-1.5 py-0.5 text-caption font-medium text-brand transition-colors duration-120 ease-out hover:bg-brand-tint hover:text-brand-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]"
          >
            Open thread
            <ArrowUpRight className="size-3" aria-hidden="true" />
          </button>
        </footer>
      </PopoverContent>
    </Popover>
  )
}

function MessageRow({
  message,
  findings,
  onOpen,
}: {
  message: Message
  findings: Finding[]
  onOpen: () => void
}): JSX.Element {
  const finding = message.findingId ? findings.find((f) => f.id === message.findingId) : null
  const tag = SIDE_TAG[message.authorSide]
  const isAi = message.authorSide === "ai"
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full items-start gap-2.5 px-4 py-3 text-left transition-colors duration-120 ease-out hover:bg-surface-sunken/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[color:var(--brand)]"
      >
        <span
          className={cn(
            "mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-sm px-1.5 py-0.5 font-id text-[10px] font-medium uppercase tracking-[0.06em]",
            tag.className,
          )}
        >
          {isAi && <Sparkles className="size-2.5" aria-hidden="true" />}
          {tag.label}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-1.5 text-caption text-text-3">
            <span className="truncate font-medium text-text-2">{message.authorName}</span>
            <span aria-hidden="true">·</span>
            <span className="font-id tabular-nums">{formatTime(message.at)}</span>
            {finding && (
              <>
                <span aria-hidden="true">·</span>
                <span className="truncate font-id">{finding.ruleId}</span>
              </>
            )}
          </span>
          <span className="mt-1 line-clamp-2 block text-body-sm leading-[1.5] text-text">
            {message.body}
          </span>
        </span>
      </button>
    </li>
  )
}

function formatTime(iso: string): string {
  const then = new Date(iso).getTime()
  const now = Date.now()
  const mins = Math.round((now - then) / 60_000)
  if (mins < 1) return "just now"
  if (mins < 60) return `${mins}m`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.round(hours / 24)
  if (days < 7) return `${days}d`
  return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" })
}
